import { defineMutator, defineMutators, type Transaction } from '@rocicorp/zero'
import { z } from 'zod'
import { recordPayment, type SqlClient } from '../../server/settlement.ts'
import { fingerprintBankRow } from '../lib/bank-import/fingerprint.ts'
import { isUtcCalendarDate } from '../lib/bank-import/dates.ts'
import { normalizeFeeDescription } from '../lib/fine-import/duplicates.ts'
import { DAY_MS } from '../lib/fine-import/parse.ts'
import { planSettlement } from '../lib/settlement.ts'
import type { AuthData } from './auth.ts'
import { builder } from './builder.ts'
import type { Schema } from './schema.ts'

// ─────────────────────────────────────────────────────────────────────────────
// Custom mutators. These are ISOMORPHIC: they run optimistically on the client
// (against the local store) and authoritatively on the server (against Postgres
// via the db-provider), using only `tx.mutate` / `tx.run`. All authorization
// lives HERE in code — named queries enforce the corresponding read rules.
// ─────────────────────────────────────────────────────────────────────────────

/** Ordering actors: members may order for themselves; kiosk/admin for anyone. */
function assertCanOrderFor(ctx: AuthData | undefined, userId: string): void {
  if (!ctx) throw new Error('unauthenticated')
  if (
    ctx.role !== 'kiosk' &&
    ctx.role !== 'admin' &&
    !(ctx.role === 'member' && ctx.sub === userId)
  ) {
    throw new Error('forbidden: cannot order for this member')
  }
}

/** Account management: the owning member or an admin, never the kiosk. */
function assertOwnerOrAdmin(ctx: AuthData | undefined, userId: string): void {
  if (!ctx) throw new Error('unauthenticated')
  if (ctx.role !== 'admin' && !(ctx.role === 'member' && ctx.sub === userId)) {
    throw new Error('forbidden: owner or admin only')
  }
}

/** Admin-only actions (levying fees, editing the catalogue). */
function assertAdmin(ctx: AuthData | undefined): asserts ctx is AuthData {
  if (!ctx) throw new Error('unauthenticated')
  if (ctx.role !== 'admin') throw new Error('forbidden: admin only')
}

function assertFeeDate(feeDate: number): void {
  if (!Number.isInteger(feeDate) || feeDate < 0 || feeDate % DAY_MS !== 0) {
    throw new Error('fee date must be a UTC calendar date')
  }
}

const feeTypeSchema = z.enum(['Fine', 'Registration', 'Expense'])
// Trimmed and non-empty, matching the guests_name_not_blank check constraint.
const guestNameSchema = z.string().trim().min(1).max(200)

// ── Member payload (US-18) ──────────────────────────────────────────────────
// Optional text fields use `.optional()` rather than `.nullish()` so an ABSENT
// key means "leave unchanged" on update, while an empty string means "clear".
// The mutator maps '' to null; the two are not interchangeable here.
const optionalText = (max: number) => z.string().trim().max(max).optional()

const memberPayloadSchema = z.object({
  displayName: z.string().trim().min(1).max(200),
  pin: z.string().regex(/^\d{4}$/),
  fullName: optionalText(200),
  email: z
    .string()
    .trim()
    .max(320)
    .refine((v) => v === '' || z.email().safeParse(v).success, 'enter a valid email address')
    .optional(),
  dateOfBirth: z.number().int().nonnegative().nullish(),
  nameOnBack: optionalText(200),
  numberOnBack: z.number().int().min(0).max(99).nullish(),
  address: optionalText(500),
  mobileNumber: optionalText(50),
  medicalInfo: optionalText(2_000),
  emergencyContactName: optionalText(200),
  emergencyContactPhone: optionalText(50),
  shirtSize: optionalText(20),
  shortSize: optionalText(20),
  shoeSize: optionalText(20),
  isAdmin: z.boolean(),
  isApproved: z.boolean(),
})

/** '' clears the field; anything else is stored trimmed. */
function textOrNull(value: string | undefined): string | null {
  return value ? value : null
}

/** Jersey names collide case- and whitespace-insensitively, as the index does. */
function jerseyKey(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toUpperCase() : null
}

function assertDateOfBirth(value: number | null | undefined): void {
  if (value == null) return
  if (!isUtcCalendarDate(value) || value < Date.UTC(1900, 0, 1) || value > Date.now()) {
    throw new Error('date of birth must be a past UTC calendar date')
  }
}

/**
 * A PIN is unique across every row — members, soft-deleted members, and the
 * reserved kiosk row. The client cannot see the reserved row, so this passes
 * optimistically and the server (or the `pins` primary key) rejects it.
 */
async function assertPinAvailable(
  tx: Transaction<Schema>,
  pinCode: string,
  userId: string,
): Promise<void> {
  const existing = await tx.run(builder.pins.where('pinCode', pinCode).one())
  if (existing && existing.userId !== userId) {
    throw new Error('that PIN is already in use or reserved, including by deleted members')
  }
}

/** Jersey identity is unique among ACTIVE members; deleted rows keep theirs. */
async function assertJerseyAvailable(
  tx: Transaction<Schema>,
  userId: string,
  nameOnBack: string | null,
  numberOnBack: number | null,
): Promise<void> {
  const key = jerseyKey(nameOnBack)
  if (key == null && numberOnBack == null) return

  const actives = await tx.run(builder.users.where('deletedAt', 'IS', null))
  for (const other of actives) {
    if (other.id === userId) continue
    if (key != null && jerseyKey(other.nameOnBack) === key) {
      throw new Error('that jersey name is taken')
    }
    if (numberOnBack != null && other.numberOnBack === numberOnBack) {
      throw new Error('that jersey number is taken')
    }
  }
}
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/)
const rawBankDataSchema = z.record(z.string().max(100), z.string().max(2_000))
const bankImportRowSchema = z.object({
  transactionId: z.string().min(1).max(100),
  paymentId: z.string().min(1).max(100),
  creditId: z.string().min(1).max(100),
  rowNumber: z.number().int().positive(),
  transactionDate: z.number().int().nonnegative(),
  amountCents: z.number().int().positive(),
  userId: z.string().min(1).max(100),
  matchConfidence: z.number().int().min(0).max(10_000),
  sourceFingerprint: sha256Schema,
  otherParty: z.string().trim().min(1).max(500),
  reference: z.string().max(1_000),
  particulars: z.string().max(1_000),
  analysisCode: z.string().max(1_000),
  description: z.string().max(2_000),
  rawData: rawBankDataSchema,
})

type BankImportMutationRow = z.infer<typeof bankImportRowSchema>

function assertUnique<T>(values: readonly T[], label: string): void {
  if (new Set(values).size !== values.length) throw new Error(`${label} must be unique`)
}

function assertBankImportDate(value: number): void {
  if (
    !isUtcCalendarDate(value) ||
    value < Date.UTC(2000, 0, 1) ||
    value >= Date.UTC(2101, 0, 1)
  ) {
    throw new Error('statement date is outside the supported range')
  }
}

function assertRawDataSize(row: BankImportMutationRow): void {
  if (Object.keys(row.rawData).length > 100) {
    throw new Error(`CSV row ${row.rowNumber} contains too many columns`)
  }
  if (JSON.stringify(row.rawData).length > 20_000) {
    throw new Error(`CSV row ${row.rowNumber} is too large`)
  }
}

async function assertFingerprint(row: BankImportMutationRow): Promise<void> {
  const expected = await fingerprintBankRow({
    transactionDate: row.transactionDate,
    amountCents: row.amountCents,
    otherParty: row.otherParty,
    reference: row.reference,
    particulars: row.particulars,
    analysisCode: row.analysisCode,
    description: row.description,
  })
  if (expected !== row.sourceFingerprint) {
    throw new Error(`CSV row ${row.rowNumber} fingerprint is invalid`)
  }
}

async function optimisticBankSettlement(
  tx: Transaction<Schema>,
  row: BankImportMutationRow,
  settledAt: number,
): Promise<void> {
  const fees = await tx.run(
    builder.fees
      .where('userId', row.userId)
      .where('deletedAt', 'IS', null)
      .where('paidAt', 'IS', null)
      .orderBy('feeDate', 'asc')
      .orderBy('createdAt', 'asc'),
  )
  const drinks = await tx.run(
    builder.userDrinks
      .where('userId', row.userId)
      .where('paidAt', 'IS', null)
      .related('drink')
      .orderBy('purchasedAt', 'asc'),
  )
  const plan = planSettlement(
    row.amountCents,
    fees.map((fee) => ({ id: fee.id, amountCents: fee.amountCents })),
    drinks.map((drink) => ({
      id: drink.id,
      amountCents: drink.drink?.priceCents ?? Number.MAX_SAFE_INTEGER,
    })),
  )

  await tx.mutate.payments.insert({
    paymentId: row.paymentId,
    userId: row.userId,
    amountCents: row.amountCents,
    stripeCheckoutId: null,
    paymentMethod: 'bank_transfer',
    createdAt: row.transactionDate,
    confirmedAt: settledAt,
    cancelledAt: null,
  })
  for (const feeId of plan.feeIds) {
    await tx.mutate.fees.update({ id: feeId, paidAt: settledAt, paymentId: row.paymentId })
  }
  for (const drinkId of plan.drinkIds) {
    await tx.mutate.userDrinks.update({
      id: drinkId,
      paidAt: settledAt,
      paymentId: row.paymentId,
    })
  }
  if (plan.remainderCents > 0) {
    await tx.mutate.credits.insert({
      id: row.creditId,
      userId: row.userId,
      paymentId: row.paymentId,
      amountCents: plan.remainderCents,
      remainingAmountCents: plan.remainderCents,
      description: 'Overpayment credit',
      createdAt: settledAt,
      usedAt: null,
    })
  }
}

export const mutators = defineMutators({
  drinks: {
    /** Record a drink against a member (optionally for one of their guests). */
    order: defineMutator(
      z.object({
        id: z.string(),
        userId: z.string(),
        guestId: z.string().nullish(),
        drinkId: z.string(),
      }),
      async ({ tx, ctx, args }) => {
        assertCanOrderFor(ctx, args.userId)

        const member = await tx.run(builder.users.where('id', args.userId).one())
        if (!member || !member.isApproved || member.deletedAt != null) {
          throw new Error('member is not available for ordering')
        }

        const drink = await tx.run(builder.drinks.where('drinkId', args.drinkId).one())
        if (!drink || drink.deletedAt != null) {
          throw new Error('drink is not available for ordering')
        }

        let guestName: string | null = null
        if (args.guestId) {
          const guest = await tx.run(builder.guests.where('id', args.guestId).one())
          if (!guest || guest.deletedAt != null || guest.userId !== args.userId) {
            throw new Error('guest is not available for ordering')
          }
          guestName = guest.name
        }

        const purchasedAt = Date.now()
        await tx.mutate.userDrinks.insert({
          id: args.id,
          userId: args.userId,
          guestId: args.guestId ?? null,
          drinkId: args.drinkId,
          purchasedAt,
          paidAt: null,
          paymentId: null,
        })
        await tx.mutate.orderActivity.insert({
          id: args.id,
          userId: args.userId,
          memberName: member.displayName,
          guestName,
          drinkDescription: drink.description,
          purchasedAt,
        })
      },
    ),

    /** Remove an UNPAID drink you own (mirrors the original's unpaid-only delete). */
    remove: defineMutator(
      z.object({ id: z.string() }),
      async ({ tx, ctx, args }) => {
        const existing = await tx.run(builder.userDrinks.where('id', args.id).one())
        if (!existing) throw new Error('drink not found')
        if (existing.paidAt != null || existing.paymentId != null) {
          throw new Error('cannot remove a drink that has been paid')
        }
        assertOwnerOrAdmin(ctx, existing.userId)
        const activity = await tx.run(builder.orderActivity.where('id', args.id).one())
        if (activity) await tx.mutate.orderActivity.delete({ id: args.id })
        await tx.mutate.userDrinks.delete({ id: args.id })
      },
    ),
  },

  fees: {
    /** Levy a fee on a member (admin only). */
    add: defineMutator(
      z.object({
        id: z.string().min(1),
        userId: z.string(),
        amountCents: z.number().int().positive(),
        description: z.string().max(500).nullish(),
        type: feeTypeSchema,
        feeDate: z.number().int().nonnegative(),
      }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        assertFeeDate(args.feeDate)
        const member = await tx.run(builder.users.where('id', args.userId).one())
        if (!member || member.deletedAt != null) {
          throw new Error('member is not available for fees')
        }

        const now = Date.now()
        const description = args.description?.trim() || null
        await tx.mutate.fees.insert({
          id: args.id,
          userId: args.userId,
          amountCents: args.amountCents,
          description,
          type: args.type,
          createdAt: now,
          feeDate: args.feeDate,
          deletedAt: null,
          paidAt: null,
          paymentId: null,
        })
      },
    ),

    /** Soft-delete an outstanding fee (admin only). */
    remove: defineMutator(
      z.object({ id: z.string().min(1) }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        const fee = await tx.run(builder.fees.where('id', args.id).one())
        if (!fee || fee.deletedAt != null) throw new Error('fee not found')
        if (fee.paidAt != null || fee.paymentId != null) {
          throw new Error('cannot remove a paid fee')
        }
        await tx.mutate.fees.update({ id: fee.id, deletedAt: Date.now() })
      },
    ),

    /** Atomically import a reviewed batch of fines with an optimistic local write. */
    import: defineMutator(
      z.object({
        feeDate: z.number().int().nonnegative(),
        rows: z.array(
          z.object({
            id: z.string().min(1),
            userId: z.string().min(1),
            amountCents: z.number().int().positive(),
            description: z.string().trim().min(1).max(500),
            type: feeTypeSchema,
            allowDuplicate: z.boolean(),
          }),
        ).min(1).max(500),
      }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        assertFeeDate(args.feeDate)
        const end = args.feeDate + DAY_MS
        const createdAt = Date.now()
        const batchKeys = new Set<string>()

        for (const row of args.rows) {
          const member = await tx.run(builder.users.where('id', row.userId).one())
          if (!member || member.deletedAt != null) {
            throw new Error(`member is not available for imported row ${row.id}`)
          }

          const normalizedDescription = normalizeFeeDescription(row.description)
          const duplicateKey = `${row.userId}\u0000${normalizedDescription}`
          const existingFees = await tx.run(
            builder.fees
              .where('userId', row.userId)
              .where('deletedAt', 'IS', null)
              .where('feeDate', '>=', args.feeDate)
              .where('feeDate', '<', end),
          )
          const hasExistingDuplicate = existingFees.some(
            (fee) => normalizeFeeDescription(fee.description) === normalizedDescription,
          )
          const hasBatchDuplicate = batchKeys.has(duplicateKey)
          if ((hasExistingDuplicate || hasBatchDuplicate) && !row.allowDuplicate) {
            throw new Error(`duplicate fee found for imported row ${row.id}`)
          }

          batchKeys.add(duplicateKey)
          await tx.mutate.fees.insert({
            id: row.id,
            userId: row.userId,
            amountCents: row.amountCents,
            description: row.description.trim(),
            type: row.type,
            createdAt,
            feeDate: args.feeDate,
            deletedAt: null,
            paidAt: null,
            paymentId: null,
          })
        }
      },
    ),
  },

  bankImports: {
    /** Reconcile a reviewed statement as one atomic, optimistic batch. */
    confirm: defineMutator(
      z.object({
        batchId: z.string().min(1).max(100),
        fileName: z.string().trim().min(1).max(255),
        sourceSha256: sha256Schema,
        rows: z.array(bankImportRowSchema).min(1).max(2_000),
      }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        assertUnique(args.rows.map((row) => row.transactionId), 'transaction ids')
        assertUnique(args.rows.map((row) => row.paymentId), 'payment ids')
        assertUnique(args.rows.map((row) => row.creditId), 'credit ids')
        assertUnique(args.rows.map((row) => row.rowNumber), 'CSV row numbers')

        for (const row of args.rows) {
          assertBankImportDate(row.transactionDate)
          assertRawDataSize(row)
          await assertFingerprint(row)
        }

        const rows = [...args.rows].sort((a, b) => a.rowNumber - b.rowNumber)
        const now = Date.now()

        if (tx.location === 'server') {
          const db = tx.dbTransaction
          const userIds = [...new Set(rows.map((row) => row.userId))].sort()
          for (const userId of userIds) {
            await db.query(
              'select pg_advisory_xact_lock(hashtextextended($1, 0))',
              [userId],
            )
          }

          for (const row of rows) {
            const memberRows = Array.from(
              await db.query(
                'select id from users where id = $1 and deleted_at is null limit 1',
                [row.userId],
              ),
            )
            if (memberRows.length === 0) {
              throw new Error(`member is not available for CSV row ${row.rowNumber}`)
            }
          }

          await db.query(
            `insert into import_batches (
               id, file_name, uploaded_by, uploaded_at,
               processed_at, processed_count, source_sha256
             )
             values ($1, $2, $3, $4, null, 0, $5)`,
            [args.batchId, args.fileName.trim(), ctx.sub, new Date(now), args.sourceSha256],
          )

          const sqlClient = db.wrappedTransaction as unknown as SqlClient
          for (const row of rows) {
            await recordPayment(sqlClient, {
              paymentId: row.paymentId,
              creditId: row.creditId,
              userId: row.userId,
              amountCents: row.amountCents,
              paymentMethod: 'bank_transfer',
              createdAt: new Date(row.transactionDate),
              confirmedAt: new Date(now),
              settledAt: new Date(now),
            })
            await db.query(
              `insert into import_transactions (
                 id, batch_id, row_number, transaction_date, amount_cents,
                 raw_data, source_fingerprint, matched_user_id, payment_id,
                 match_confidence, imported_at
               )
               values ($1, $2, $3, $4, $5, $6::jsonb, $7, $8, $9, $10, $11)`,
              [
                row.transactionId,
                args.batchId,
                row.rowNumber,
                new Date(row.transactionDate),
                row.amountCents,
                JSON.stringify(row.rawData),
                row.sourceFingerprint,
                row.userId,
                row.paymentId,
                row.matchConfidence,
                new Date(now),
              ],
            )
          }

          await db.query(
            `update import_batches
                set processed_at = $2, processed_count = $3
              where id = $1`,
            [args.batchId, new Date(now), rows.length],
          )
          return
        }

        await tx.mutate.importBatches.insert({
          id: args.batchId,
          fileName: args.fileName.trim(),
          uploadedBy: ctx.sub,
          uploadedAt: now,
          processedAt: null,
          processedCount: 0,
          sourceSha256: args.sourceSha256,
        })

        for (const row of rows) {
          const member = await tx.run(builder.users.where('id', row.userId).one())
          if (!member || member.deletedAt != null) {
            throw new Error(`member is not available for CSV row ${row.rowNumber}`)
          }
          await optimisticBankSettlement(tx, row, now)
          await tx.mutate.importTransactions.insert({
            id: row.transactionId,
            batchId: args.batchId,
            rowNumber: row.rowNumber,
            transactionDate: row.transactionDate,
            amountCents: row.amountCents,
            rawData: row.rawData,
            sourceFingerprint: row.sourceFingerprint,
            matchedUserId: row.userId,
            paymentId: row.paymentId,
            matchConfidence: row.matchConfidence,
            importedAt: now,
          })
        }
        await tx.mutate.importBatches.update({
          id: args.batchId,
          processedAt: now,
          processedCount: rows.length,
        })
      },
    ),
  },

  guests: {
    /** Add a guest under a member (that member or an admin). */
    add: defineMutator(
      z.object({
        id: z.string().min(1),
        userId: z.string().min(1),
        name: guestNameSchema,
      }),
      async ({ tx, ctx, args }) => {
        assertOwnerOrAdmin(ctx, args.userId)
        const member = await tx.run(builder.users.where('id', args.userId).one())
        if (!member || !member.isApproved || member.deletedAt != null) {
          throw new Error('member is not available for guests')
        }
        await tx.mutate.guests.insert({
          id: args.id,
          userId: args.userId,
          name: args.name.trim(),
          buttonPosition: null,
          createdAt: Date.now(),
          deletedAt: null,
        })
      },
    ),

    /** Rename a guest (that guest's owner or an admin). */
    rename: defineMutator(
      z.object({ id: z.string().min(1), name: guestNameSchema }),
      async ({ tx, ctx, args }) => {
        const guest = await tx.run(builder.guests.where('id', args.id).one())
        if (!guest || guest.deletedAt != null) throw new Error('guest not found')
        // Authorize against the LOADED row, so a guessed id cannot be renamed.
        assertOwnerOrAdmin(ctx, guest.userId)
        await tx.mutate.guests.update({ id: guest.id, name: args.name.trim() })
      },
    ),

    /**
     * Soft-delete a guest (that guest's owner or an admin). Never a hard delete:
     * US-13 requires historical purchases to survive, and user_drinks.guest_id
     * references this row. The unique button slot is released so it can be
     * reused (USER_STORIES decision register item 12).
     */
    remove: defineMutator(
      z.object({ id: z.string().min(1) }),
      async ({ tx, ctx, args }) => {
        const guest = await tx.run(builder.guests.where('id', args.id).one())
        if (!guest || guest.deletedAt != null) throw new Error('guest not found')
        assertOwnerOrAdmin(ctx, guest.userId)
        await tx.mutate.guests.update({
          id: guest.id,
          deletedAt: Date.now(),
          buttonPosition: null,
        })
      },
    ),
  },

  members: {
    /** Create a member across users, users_private, users_profile and pins. */
    create: defineMutator(
      memberPayloadSchema.extend({ userId: z.string().min(1).max(100) }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        assertDateOfBirth(args.dateOfBirth)

        const nameOnBack = textOrNull(args.nameOnBack)
        const numberOnBack = args.numberOnBack ?? null
        await assertPinAvailable(tx, args.pin, args.userId)
        await assertJerseyAvailable(tx, args.userId, nameOnBack, numberOnBack)

        await tx.mutate.users.insert({
          id: args.userId,
          displayName: args.displayName,
          nameOnBack,
          numberOnBack,
          buttonPosition: null,
          isAdmin: args.isAdmin,
          isApproved: args.isApproved,
          createdAt: Date.now(),
          deletedAt: null,
        })
        await tx.mutate.usersPrivate.insert({
          userId: args.userId,
          fullName: textOrNull(args.fullName),
          email: textOrNull(args.email),
          dateOfBirth: args.dateOfBirth ?? null,
        })
        await tx.mutate.usersProfile.insert({
          userId: args.userId,
          address: textOrNull(args.address),
          mobileNumber: textOrNull(args.mobileNumber),
          medicalInfo: textOrNull(args.medicalInfo),
          emergencyContactName: textOrNull(args.emergencyContactName),
          emergencyContactPhone: textOrNull(args.emergencyContactPhone),
          shirtSize: textOrNull(args.shirtSize),
          shortSize: textOrNull(args.shortSize),
          shoeSize: textOrNull(args.shoeSize),
        })
        await tx.mutate.pins.insert({
          pinCode: args.pin,
          userId: args.userId,
          reason: null,
        })
      },
    ),

    /**
     * Update the submitted fields only. An absent key leaves a field alone; an
     * empty string clears it. Uniqueness is re-checked only for what changed.
     */
    update: defineMutator(
      memberPayloadSchema.partial().extend({ userId: z.string().min(1).max(100) }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        assertDateOfBirth(args.dateOfBirth)

        const member = await tx.run(builder.users.where('id', args.userId).one())
        if (!member || member.deletedAt != null) throw new Error('member not found')

        if (args.nameOnBack !== undefined || args.numberOnBack !== undefined) {
          await assertJerseyAvailable(
            tx,
            args.userId,
            args.nameOnBack !== undefined ? textOrNull(args.nameOnBack) : member.nameOnBack ?? null,
            args.numberOnBack !== undefined ? args.numberOnBack ?? null : member.numberOnBack ?? null,
          )
        }

        const userPatch: {
          id: string
          displayName?: string
          nameOnBack?: string | null
          numberOnBack?: number | null
          isAdmin?: boolean
          isApproved?: boolean
        } = { id: args.userId }
        if (args.displayName !== undefined) userPatch.displayName = args.displayName
        if (args.nameOnBack !== undefined) userPatch.nameOnBack = textOrNull(args.nameOnBack)
        if (args.numberOnBack !== undefined) userPatch.numberOnBack = args.numberOnBack ?? null
        if (args.isAdmin !== undefined) userPatch.isAdmin = args.isAdmin
        if (args.isApproved !== undefined) userPatch.isApproved = args.isApproved
        if (Object.keys(userPatch).length > 1) await tx.mutate.users.update(userPatch)

        const privatePatch: {
          userId: string
          fullName?: string | null
          email?: string | null
          dateOfBirth?: number | null
        } = { userId: args.userId }
        if (args.fullName !== undefined) privatePatch.fullName = textOrNull(args.fullName)
        if (args.email !== undefined) privatePatch.email = textOrNull(args.email)
        if (args.dateOfBirth !== undefined) privatePatch.dateOfBirth = args.dateOfBirth ?? null
        if (Object.keys(privatePatch).length > 1) {
          await tx.mutate.usersPrivate.update(privatePatch)
        }

        const profilePatch: {
          userId: string
          address?: string | null
          mobileNumber?: string | null
          medicalInfo?: string | null
          emergencyContactName?: string | null
          emergencyContactPhone?: string | null
          shirtSize?: string | null
          shortSize?: string | null
          shoeSize?: string | null
        } = { userId: args.userId }
        if (args.address !== undefined) profilePatch.address = textOrNull(args.address)
        if (args.mobileNumber !== undefined) {
          profilePatch.mobileNumber = textOrNull(args.mobileNumber)
        }
        if (args.medicalInfo !== undefined) {
          profilePatch.medicalInfo = textOrNull(args.medicalInfo)
        }
        if (args.emergencyContactName !== undefined) {
          profilePatch.emergencyContactName = textOrNull(args.emergencyContactName)
        }
        if (args.emergencyContactPhone !== undefined) {
          profilePatch.emergencyContactPhone = textOrNull(args.emergencyContactPhone)
        }
        if (args.shirtSize !== undefined) profilePatch.shirtSize = textOrNull(args.shirtSize)
        if (args.shortSize !== undefined) profilePatch.shortSize = textOrNull(args.shortSize)
        if (args.shoeSize !== undefined) profilePatch.shoeSize = textOrNull(args.shoeSize)
        if (Object.keys(profilePatch).length > 1) {
          await tx.mutate.usersProfile.update(profilePatch)
        }

        // `pin_code` is the primary key, so a change is delete-then-insert.
        // Both sit in the mutator's one transaction — the member is never
        // momentarily without a PIN.
        if (args.pin !== undefined) {
          const current = await tx.run(builder.pins.where('userId', args.userId).one())
          if (current?.pinCode !== args.pin) {
            await assertPinAvailable(tx, args.pin, args.userId)
            if (current) await tx.mutate.pins.delete({ pinCode: current.pinCode })
            await tx.mutate.pins.insert({
              pinCode: args.pin,
              userId: args.userId,
              reason: null,
            })
          }
        }
      },
    ),

    /**
     * Soft-delete a member. The record stays intact: the jersey is freed by the
     * partial unique indexes rather than by blanking it, and the PIN stays
     * reserved. Only the table-wide unique button slot is cleared. Financial
     * history is untouched.
     */
    remove: defineMutator(
      z.object({ userId: z.string().min(1).max(100) }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        if (args.userId === ctx.sub) {
          throw new Error('you cannot delete your own account')
        }

        const member = await tx.run(builder.users.where('id', args.userId).one())
        if (!member || member.deletedAt != null) throw new Error('member not found')

        await tx.mutate.users.update({
          id: args.userId,
          deletedAt: Date.now(),
          isAdmin: false,
          isApproved: false,
          buttonPosition: null,
        })

        // auth_sessions is server-only and absent from the Zero schema, so this
        // closes the live-session window on the authoritative side only. The
        // client branch does not need it: the next /api/session refresh fails
        // anyway once the member is deleted.
        if (tx.location === 'server') {
          await tx.dbTransaction.query(
            `update auth_sessions set revoked_at = now()
              where user_id = $1 and revoked_at is null`,
            [args.userId],
          )
        }
      },
    ),
  },

  catalogue: {
    /** Add a drink (admin only). */
    create: defineMutator(
      z.object({
        drinkId: z.string().min(1).max(100),
        description: z.string().trim().min(1).max(200),
        priceCents: z.number().int().positive(),
      }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        await tx.mutate.drinks.insert({
          drinkId: args.drinkId,
          description: args.description,
          priceCents: args.priceCents,
          buttonPosition: null,
          createdAt: Date.now(),
          deletedAt: null,
        })
      },
    ),

    /**
     * Change a drink's description (admin only).
     *
     * US-19: a price cannot change after creation. There is no price argument
     * and none is passed to `update`, and `catalogue.create` is the only
     * mutator that ever writes `priceCents` — so no application path can
     * restate what a historical purchase cost.
     */
    rename: defineMutator(
      z.object({
        drinkId: z.string().min(1).max(100),
        description: z.string().trim().min(1).max(200),
      }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        const drink = await tx.run(builder.drinks.where('drinkId', args.drinkId).one())
        if (!drink || drink.deletedAt != null) throw new Error('drink not found')
        await tx.mutate.drinks.update({
          drinkId: drink.drinkId,
          description: args.description,
        })
      },
    ),

    /**
     * Soft-delete a drink (admin only), freeing its button slot. Never a hard
     * delete: user_drinks.drink_id references the row, and historical purchases
     * must keep their original drink and price.
     */
    remove: defineMutator(
      z.object({ drinkId: z.string().min(1).max(100) }),
      async ({ tx, ctx, args }) => {
        assertAdmin(ctx)
        const drink = await tx.run(builder.drinks.where('drinkId', args.drinkId).one())
        if (!drink || drink.deletedAt != null) throw new Error('drink not found')
        await tx.mutate.drinks.update({
          drinkId: drink.drinkId,
          deletedAt: Date.now(),
          buttonPosition: null,
        })
      },
    ),
  },
})
